import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { UNKNOWN_ERROR_MESSAGE } from '../../../constants/commonConstants';
import {
  replaceTokens,
  replaceTokensInString,
  getCookie
} from '../../../utils';
import { format } from 'date-fns';
import { CatInlineNotification } from '@blocks-react/components/InlineNotification';
import { CatIconWarningDiamondFilled } from '@blocks-react/components/Icons/WarningDiamondFilled';
import { copyToClipboard } from '@app/components/pages/checkout/utils';
import { useState } from 'react';
import ExceptionBlocksFeedbackActions from './ExceptionBlocksFeedbackActions';
import { CatIconCopy } from '@blocks-react/components/Icons/Copy';
import { CatIconSuccess } from '@blocks-react/components/Icons/Success';

const ExceptionBlocksFeedbackBySelector = ({
  className,
  children,
  errorCodeLabel,
  hideTitle,
  selector,
  showContactUs = false,
  skipTranslation,
  hideErrorCode,
  showDate = false,
  showRefresh = false,
  showReferenceId = false
}) => {
  const { t } = useTranslation();
  const error = useSelector(selector);
  const formattedDate = format(new Date(), "MMMM d, yyyy 'at' h:mma");
  const referenceId = getCookie('X-Cat-Pcc-Session-Id');

  const [alreadyClicked, setAlreadyClicked] = useState(false);

  const {
    actions,
    code,
    message,
    title,
    messageParams,
    severity,
    detailedError
  } = error || {};

  const getTitle = () => {
    if (hideTitle) {
      return undefined;
    }
    return (skipTranslation ? title : t(title)) || title;
  };

  const getDetailedError = () => {
    if (!detailedError) {
      return '';
    }
    return `[${t(detailedError)}]`;
  };

  const getFormattedCode = () => {
    if (!code || hideErrorCode) {
      return '';
    }
    return ` (${
      errorCodeLabel ? replaceTokensInString(t(errorCodeLabel), code) : code
    })`;
  };

  const getMessage = (m, params = [], shouldSkipTranslation = false) => {
    const selectedMessage = (shouldSkipTranslation ? m : t(m, m)) || m;
    const msg = m
      ? replaceTokens(selectedMessage, params)
      : t(UNKNOWN_ERROR_MESSAGE);
    return (
      <p
        key={msg}
        style={{ lineHeight: '20px', marginTop: '4px' }}
      >{`${msg}${getFormattedCode()}`}</p>
    );
  };

  const handleCopyReferenceId = () => {
    copyToClipboard(referenceId);
    setAlreadyClicked(true);
  };

  return (
    ((!hideTitle && !!title) || !!message) && (
      <div className={className}>
        <CatInlineNotification
          heading={`${getTitle() || ''} ${getDetailedError() || ''}`}
          variant={severity ?? 'error'}
        >
          <CatIconWarningDiamondFilled slot="before" />
          {Array.isArray(message) && message.map(m => getMessage(m))}
          {typeof message === 'string' &&
            getMessage(message, messageParams, skipTranslation)}
          {children}
          {showReferenceId && (
            <div
              className="d-flex gap-3 mb-3"
              style={{ color: alreadyClicked ? '#AAAAAA' : '#0078D6' }}
            >
              <div>
                {t('SHOPPING_CART_REFERENCE_ID')}: {referenceId}
              </div>
              <div>
                {!alreadyClicked ? (
                  <CatIconCopy
                    style={{ cursor: 'pointer' }}
                    onClick={handleCopyReferenceId}
                  />
                ) : (
                  <CatIconSuccess />
                )}
              </div>
            </div>
          )}
          <ExceptionBlocksFeedbackActions
            actions={actions}
            showContactUs={showContactUs}
            showRefresh={showRefresh}
            referenceId={referenceId}
          />
          {showDate && (
            <div className="mt-2 mb-n2">
              <p className="u-color--MediumGray text-xs">{formattedDate}</p>
            </div>
          )}
        </CatInlineNotification>
      </div>
    )
  );
};

ExceptionBlocksFeedbackBySelector.propTypes = {
  className: PropTypes.string,
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node
  ]),
  errorCodeLabel: PropTypes.string,
  hideTitle: PropTypes.bool,
  selector: PropTypes.func.isRequired,
  showContactUs: PropTypes.bool,
  skipTranslation: PropTypes.bool,
  hideErrorCode: PropTypes.bool,
  showDate: PropTypes.bool,
  showRefresh: PropTypes.bool,
  showReferenceId: PropTypes.bool
};

export default ExceptionBlocksFeedbackBySelector;
